import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { roleGuard } from './guards/AuthGuard';
import LazyLoadingSuspense, { LazyLoadingFallback } from './components/shared-components/loading/LazyLoadingSuspense';

// Lazy load all components for code splitting
// Auth Components
const Login = React.lazy(() => import('./components/auth/Login'));
const Signup = React.lazy(() => import('./components/auth/Signup'));
const Passwordreset = React.lazy(() => import('./components/auth/PasswordReset'));
const ChangePasswordNoAuth = React.lazy(() => import('./components/auth/ChangePassword'));
const VerifyEmail = React.lazy(() => import('./components/auth/VerifyEmail'));

// Layout Components
const StickLayout = React.lazy(() => import('./layout/StickLayout'));
const Selector = React.lazy(() => import('./components/selector/Selector'));

// Dashboard Components
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));

// Product Components
const ListProducts = React.lazy(() => import('./components/products/list-products/ListProducts').then(module => ({ default: module.ListProducts })));
const CreateProduct = React.lazy(() => import('./components/products/create-product/CreateProduct'));
const ProductDashboard = React.lazy(() => import('./components/products/dashboard/ProductDashboard').then(module => ({ default: module.ProductDashboard })));
const UpdateProduct = React.lazy(() => import('./components/products/update'));

// Release Components
const CreateRelease = React.lazy(() => import('./components/releases/createRelease'));
const ListReleases = React.lazy(() => import('./components/releases/list-releases/ListReleases').then(module => ({ default: module.ListReleases })));
const ReleaseManifest = React.lazy(() => import('./components/releases/release-manifest/ReleaseManifest').then(module => ({ default: module.ReleaseManifest })));
const CommitRelease = React.lazy(() => import('./components/releases/commit-release/CommitRelease').then(module => ({ default: module.CommitRelease })));
const ReleaseVoting = React.lazy(() => import('./components/releases/release-voting/ReleaseVoting').then(module => ({ default: module.ReleaseVoting })));
const UpdateRelease = React.lazy(() => import('./components/releases/updateRelease/UpdateRelease'));
const ManageRelease = React.lazy(() => import('./components/releases/manage-release/ManageRelease'));

// Release Topics Components
const CreateReleaseTopics = React.lazy(() => import('./components/releases-topics/createReleaseTopics'));
const ListReleaseTopics = React.lazy(() => import('./components/releases-topics/list-release-topics/ListReleaseTopics').then(module => ({ default: module.ListReleaseTopics })));
const Details = React.lazy(() => import('./components/releases-topics/details/Details').then(module => ({ default: module.Details })));
const TopicGrooming = React.lazy(() => import('./components/releases-topics/topic-grooming/TopicGrooming'));
const UpdateReleaseTopic = React.lazy(() => import('./components/releases-topics/update'));

// Task Management Components
const TaskList = React.lazy(() => import('./components/project-mgmt/tasks/TaskList'));
const CreateTask = React.lazy(() => import('./components/project-mgmt/tasks/CreateTask'));
const TaskDetail = React.lazy(() => import('./components/project-mgmt/tasks/TaskDetail'));

// User Group Components
const CreateUserGroup = React.lazy(() => import('./components/user-groups/create-user-group/CreateUserGroup'));
const AddUser = React.lazy(() => import('./components/user-groups/add-user/AddUser'));
const ListUserGroups = React.lazy(() => import('./components/user-groups/list-user-group/ListUserGroups'));
const ListUsersByGroup = React.lazy(() => import('./components/user-groups/list-users/ListUsersByGroup'));

// User Components
const Users = React.lazy(() => import('./components/users/list-users/Users'));
const AddNewUser = React.lazy(() => import('./components/users/add-user/AddNewUser'));
const ChangeUserPassword = React.lazy(() => import('./components/users/ChangePassword/ChangeUserPassword'));

// Project Components
const CreateProject = React.lazy(() => import('./components/projects/createProject/CreateProject'));
const ListProjects = React.lazy(() => import('./components/projects/list-projects/ListProjects').then(module => ({ default: module.ListProjects })));


// Roadmap Components
const ListRoadMap = React.lazy(() => import('./components/roadmaps/listRoadMap/ListRoadMap').then(module => ({ default: module.ListRoadMap })));
const CreateRoadMap = React.lazy(() => import('./components/roadmaps/createRoadMap/CreateRoadMap'));
const UpdateRoadMap = React.lazy(() => import('./components/roadmaps/update'));
const ListRoadMapMilestoneView = React.lazy(() => import('./components/roadmaps/listRoadMap/ListRoadMapMilestoneView').then(module => ({ default: module.ListRoadMapMilestoneView })));

// Milestone Components
const ListMilestones = React.lazy(() => import('./components/milestones/list/ListMilestones').then(module => ({ default: module.ListMilestones })));
const CreateMilestones = React.lazy(() => import('./components/milestones/create/CreateMilestones'));
const EditMilestone = React.lazy(() => import('./components/milestones/edit/EditMilestone'));
const MilestoneReleaseTopics = React.lazy(() => import('./components/milestones/release-topics-insights/MilestoneReleaseTopics'));
const MilestoneReleaseNote = React.lazy(() => import('./components/milestones/release-notes/MilestoneReleaseNote'));
const StandupDashboard = React.lazy(() => import('./components/shared-components/standup/StandupDashboard'));

// Metrics Components
const CreateMetrics = React.lazy(() => import('./components/metrics/create-metrics/CreateMetrics'));
const ListMetrics = React.lazy(() => import('./components/metrics/list-metrics/ListMetrics').then(module => ({ default: module.ListMetrics })));
const ListKpiRecords = React.lazy(() => import('./components/metrics/list-kpi-records/ListKpiRecords').then(module => ({ default: module.ListKpiRecords })));
const ListMetricInsights = React.lazy(() => import('./components/metrics/list-metric-insights/ListMetricInsights').then(module => ({ default: module.ListMetricInsights })));
const CreateMetricInsights = React.lazy(() => import('./components/metrics/create-metric-insights/CreateMetricInsights'));



// Action Components
const ListActions = React.lazy(() => import('./components/myactions/list-actions/ListActions').then(module => ({ default: module.ListActions })));

// Notification Components
const ListNotifications = React.lazy(() => import('./components/notifications/list-notifications/ListNotifications'));

// Profile Components
const Profile = React.lazy(() => import('./components/profile/Profile'));
const Account = React.lazy(() => import('./components/account/Account'));
const ChangePassword = React.lazy(() => import('./components/profile/ChangePassword'));

// Admin Portal Components
const AccountList = React.lazy(() => import('./components/admin-portal/accounts/AccountList').then(module => ({ default: module.AccountList })));
const UserList = React.lazy(() => import('./components/admin-portal/users/UserList').then(module => ({ default: module.UserList })));

// AI Agents Components
const ControlPanel = React.lazy(() => import('./components/ai-agents/ControlPanel'));

// Marketplace Components
const StoreFront = React.lazy(() => import('./components/marketplace/StoreFront').then(module => ({ default: module.StoreFront })));

// Subscription Components
const Subscriptions = React.lazy(() => import('./components/subscriptions/Subscriptions').then(module => ({ default: module.Subscriptions })));

// Reference Resolver Component
const ReferenceResolver = React.lazy(() => import('./components/releases-topics/reference-resolver/ReferenceResolver').then(module => ({ default: module.ReferenceResolverWithErrorBoundary })));

export default function AppRoutes(props: any): React.ReactElement {
  const routes = createBrowserRouter([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <LazyLoadingSuspense fallbackText="Loading login...">
              <Login />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'signup',
          element: (
            <LazyLoadingSuspense fallbackText="Loading signup...">
              <Signup />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'password-reset',
          element: (
            <LazyLoadingSuspense fallbackText="Loading password reset...">
              <Passwordreset />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'change-password',
          element: (
            <LazyLoadingSuspense fallbackText="Loading change password...">
              <ChangePasswordNoAuth />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'verify-email',
          element: (
            <LazyLoadingSuspense fallbackText="Loading email verification...">
              <VerifyEmail />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: '',
          element: <Navigate to="/auth/login" />,
        },
      ],
    },
    {
      path: 'select',
      element: (
        <LazyLoadingSuspense fallbackText="Loading selector...">
          <Selector />
        </LazyLoadingSuspense>
      ),
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      loader: roleGuard.protectedLoader,
      children: [
        {
          path: 'dashboard',
          element: (
            <LazyLoadingSuspense fallbackText="Loading dashboard...">
              <Dashboard />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'products',
          element: (
            <LazyLoadingSuspense fallbackText="Loading products...">
              <ListProducts />
            </LazyLoadingSuspense>
          ),
        },
      ],
    },
    {
      path: ':projectId',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      loader: roleGuard.protectedLoader,
      children: [
        {
          path: 'release-agents',
          element: (
            <LazyLoadingSuspense fallbackText="Loading AI agents...">
              <ControlPanel />
            </LazyLoadingSuspense>
          ),
        }
      ],
    },
    {
      path: 'projects',
      children: [
        {
          path: 'new',
          element: (
            <LazyLoadingSuspense fallbackText="Loading project creation...">
              <CreateProject />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading projects...">
              <ListProjects />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/user-groups',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: 'new',
          element: (
            <LazyLoadingSuspense fallbackText="Loading user group creation...">
              <CreateUserGroup />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':usergroupid/add-user',
          element: (
            <LazyLoadingSuspense fallbackText="Loading add user...">
              <AddUser />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':usergroupid/users',
          element: (
            <LazyLoadingSuspense fallbackText="Loading group users...">
              <ListUsersByGroup />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading user groups...">
              <ListUserGroups />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/products',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      loader: roleGuard.protectedLoader,
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading products...">
              <ListProducts />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'create',
          element: (
            <LazyLoadingSuspense fallbackText="Loading product creation...">
              <CreateProduct />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId',
          element: (
            <LazyLoadingSuspense fallbackText="Loading product dashboard...">
              <ProductDashboard />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId/releases',
          element: (
            <LazyLoadingSuspense fallbackText="Loading releases...">
              <ListReleases />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId/release/create',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release creation...">
              <CreateRelease />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId/edit',
          element: (
            <LazyLoadingSuspense fallbackText="Loading product editor...">
              <UpdateProduct />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId/release/:releaseId',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release details...">
              <CreateRelease />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId/release/:releaseId/update',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release editor...">
              <UpdateRelease />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId/release/:releaseId/manage',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release management...">
              <ManageRelease />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId/release/:releaseId/commit',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release commit...">
              <CommitRelease />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':productId/release/:releaseId/vote/:actionId',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release voting...">
              <ReleaseVoting />
            </LazyLoadingSuspense>
          ),
        },
      ],
    },
    {
      path: ':projectId/release-topics',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release topics...">
              <ListReleaseTopics />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'list',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release topics...">
              <ListReleaseTopics />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'create',
          element: (
            <LazyLoadingSuspense fallbackText="Loading topic creation...">
              <CreateReleaseTopics />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':topicId/details',
          element: (
            <LazyLoadingSuspense fallbackText="Loading topic details...">
              <Details />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':topicId/groom',
          element: (
            <LazyLoadingSuspense fallbackText="Loading topic grooming...">
              <TopicGrooming />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':topicId/edit',
          element: (
            <LazyLoadingSuspense fallbackText="Loading topic editor...">
              <UpdateReleaseTopic />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/tasks',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading tasks...">
              <TaskList />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'create',
          element: (
            <LazyLoadingSuspense fallbackText="Loading task creation...">
              <CreateTask />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':taskId',
          element: (
            <LazyLoadingSuspense fallbackText="Loading task details...">
              <TaskDetail />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':taskId/edit',
          element: (
            <LazyLoadingSuspense fallbackText="Loading task editor...">
              <TaskDetail />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/myactions',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading your actions...">
              <ListActions />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/roadmaps',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading roadmaps...">
              <ListRoadMap />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'timelineview',
          element: (
            <LazyLoadingSuspense fallbackText="Loading timeline view...">
              <ListRoadMapMilestoneView />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'create',
          element: (
            <LazyLoadingSuspense fallbackText="Loading roadmap creation...">
              <CreateRoadMap />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':roadmapId',
          element: (
            <LazyLoadingSuspense fallbackText="Loading roadmap editor...">
              <UpdateRoadMap />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/milestones',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading milestones...">
              <ListMilestones />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'new',
          element: (
            <LazyLoadingSuspense fallbackText="Loading milestone creation...">
              <CreateMilestones />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':milestoneId/edit',
          element: (
            <LazyLoadingSuspense fallbackText="Loading milestone editor...">
              <EditMilestone />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':milestoneId/release-topics-insights',
          element: (
            <LazyLoadingSuspense fallbackText="Loading milestone insights...">
              <MilestoneReleaseTopics />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':milestoneId/standups',
          element: (
            <LazyLoadingSuspense fallbackText="Loading standup dashboard...">
              <StandupDashboard />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: ':milestoneId/release-notes',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release notes...">
              <MilestoneReleaseNote />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/release-manifest',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: ':productId/releases/:releaseId',
          element: (
            <LazyLoadingSuspense fallbackText="Loading release manifest...">
              <ReleaseManifest />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/notifications',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading notifications...">
              <ListNotifications />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: 'profile',
          element: (
            <LazyLoadingSuspense fallbackText="Loading profile...">
              <Profile />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'password-reset',
          element: (
            <LazyLoadingSuspense fallbackText="Loading password reset...">
              <ChangePassword />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'users/:userId/password-reset',
          element: (
            <LazyLoadingSuspense fallbackText="Loading user password reset...">
              <ChangeUserPassword />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: 'marketplace',
          element: (
            <LazyLoadingSuspense fallbackText="Loading marketplace...">
              <StoreFront />
            </LazyLoadingSuspense>
          ),
        }
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/account',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading account...">
              <Account />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/subscriptions',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading subscriptions...">
              <Subscriptions />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/users',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading users...">
              <Users />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'new',
          element: (
            <LazyLoadingSuspense fallbackText="Loading user creation...">
              <AddNewUser />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/metrics',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: '',
          element: (
            <LazyLoadingSuspense fallbackText="Loading metrics...">
              <ListMetrics />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'create',
          element: (
            <LazyLoadingSuspense fallbackText="Loading metric creation...">
              <CreateMetrics />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'kpi-structs/:kpiStructId/records',
          element: (
            <LazyLoadingSuspense fallbackText="Loading KPI records...">
              <ListKpiRecords />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'insights',
          element: (
            <LazyLoadingSuspense fallbackText="Loading metric insights...">
              <ListMetricInsights />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'insights/create',
          element: (
            <LazyLoadingSuspense fallbackText="Loading insight creation...">
              <CreateMetricInsights />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: ':projectId/admin',
      element: (
        <LazyLoadingSuspense fallbackText="Loading application..." minHeight="100vh">
          <StickLayout />
        </LazyLoadingSuspense>
      ),
      children: [
        {
          path: 'accounts',
          element: (
            <LazyLoadingSuspense fallbackText="Loading admin accounts...">
              <AccountList />
            </LazyLoadingSuspense>
          ),
        },
        {
          path: 'users',
          element: (
            <LazyLoadingSuspense fallbackText="Loading admin users...">
              <UserList />
            </LazyLoadingSuspense>
          ),
        },
      ],
      loader: roleGuard.protectedLoader,
    },
    {
      path: 'release-topics/:referenceId',
      element: (
        <LazyLoadingSuspense fallbackText="Resolving release topic reference...">
          <ReferenceResolver />
        </LazyLoadingSuspense>
      ),
    },
    {
      path: '',
      element: <Navigate to="/auth/login" />,
    },
    {
      path: '*',
      element: <Navigate to="/auth/login" />,
    },
  ]);

  return (
    <Suspense fallback={<LazyLoadingFallback text="Loading Release Pilot..." minHeight="100vh" />}>
      <RouterProvider router={routes} />
    </Suspense>
  );
}
